export const LANGUAGES = ['en', 'sr', 'es'] as const;

export type Language = (typeof LANGUAGES)[number];

export const I18N_STORAGE_KEY = 'functor-language';

const en = {
  common: {
    brand: 'Functor',
    close: 'Close',
    languageNames: {
      en: 'English',
      sr: 'Serbian',
      es: 'Spanish'
    },
    languageShortNames: {
      en: 'EN',
      sr: 'SR',
      es: 'ES'
    },
    countries: {
      serbia: 'Serbia',
      unitedStates: 'United States',
      croatia: 'Croatia'
    },
    nav: {
      ariaLabel: 'Main navigation',
      homeAriaLabel: 'Functor home',
      home: 'Home',
      services: 'Services',
      cases: 'Cases',
      insights: 'Insights',
      about: 'About',
      partners: 'Partners',
      contact: 'Contact',
      changeTheme: 'Change theme',
      openMenu: 'Open menu',
      languageSelector: 'Select language',
      darkMode: 'Dark mode',
      lightMode: 'Light mode'
    },
    serviceMenu: [
      {
        label: 'On-prem Datasliv Platform',
        description: 'Pipelines, warehousing and data quality.'
      },
      {
        label: 'Data Engineering',
        description: 'Pipelines, warehousing and data quality.'
      },
      {
        label: 'Managed Data Platform',
        description: 'Cloud platform operations and support.'
      },
      {
        label: 'Business Intelligence',
        description: 'Dashboards, KPIs and self-service analytics.'
      },
      {
        label: 'AI & Machine Learning',
        description: 'Prediction, automation and responsible AI.'
      }
    ],
    buttons: {
      bookIntroduction: 'Book an introduction',
      discoverExpertise: 'Discover expertise',
      viewFullCaseStudy: 'View Full Case Study',
      browseAllCaseStudies: 'Browse all case studies',
      seeEngineeringStandards: 'See our engineering standards',
      workWithUs: 'Work with us'
    },
    techPartners: [
      'Databricks',
      'Apache Spark',
      'Microsoft Azure',
      'AWS',
      'Snowflake',
      'Scala'
    ]
  },
  pageTitles: {
    home: 'Functor | AI, Data & Analytics Consultancy',
    dataEngineering: 'Data Engineering | Functor',
    managedDataPlatform: 'Managed Data Platform | Functor',
    businessIntelligence: 'Business Intelligence | Functor',
    aiMachineLearning: 'AI & Machine Learning | Functor',
    cases: 'Cases | Functor',
    insights: 'Insights | Functor',
    about: 'About Us | Functor',
    partners: 'Partners | Functor',
    contact: 'Contact | Functor',
    notFound: '404 Page Not Found | Functor',
    error: 'Something Went Wrong | Functor'
  },
  footer: {
    description:
      'Functor is a Data & AI firm specializing in modern data platforms, advanced analytics, and high-performance engineering.',
    contactUs: 'Contact Us',
    allRightsReserved: 'All rights reserved.'
  },
  backToTop: {
    ariaLabel: (progress: number) => `Back to top. Page progress ${progress}%.`
  },
  home: {
    hero: {
      eyebrow: 'Modern Data & Enterprise AI Infrastructure',
      titleLine1: 'We build modern data',
      titleLine2: 'and enterprise AI',
      titleHighlight: 'solutions.',
      description:
        'Functor designs, builds, and maintains modern data platforms and enterprise AI systems that give you full control over your infrastructure. We transform messy environments into clean, reliable automated pipelines.',
      capabilitiesAria: 'Go to capabilities section'
    },
    services: {
      eyebrow: 'Capabilities',
      title: 'Architecting core systems.',
      items: [
        {
          title: 'Data engineering',
          desc: 'Automating your data flows, cleaning up databases, and building solid pipelines so your teams always work with accurate numbers.'
        },
        {
          title: 'Managed Platform',
          desc: 'Continuous monitoring, server optimization, and full system maintenance to guarantee your data never stops moving.'
        },
        {
          title: 'Business Intelligence',
          desc: 'Turning raw data into clear, visual dashboards that align all departments and help leadership make fast, confident decisions.'
        },
        {
          title: 'AI & Machine Learning',
          desc: 'Building smart automation, trend forecasting models, and intelligent assistant workflows that create real business value.'
        }
      ]
    },
    cases: {
      titleStart: 'Proven',
      titleHighlight: 'results.',
      description:
        'We build clean data infrastructure that solves your real operational bottlenecks. Explore how our engineering directly translates into lower infrastructure costs, faster systems, and reliable business decisions.'
    },
    approach: {
      eyebrow: 'Our Core Principles',
      titleStart: 'Engineering workflows focused on',
      titleHighlight: 'long-term stability.',
      items: [
        {
          title: 'Reliable Codebases',
          text: 'We lean on clean, functional code patterns to eliminate hidden side effects. This ensures your data pipelines are completely predictable and highly resilient against production bugs.'
        },
        {
          title: 'Seamless Team Integration',
          text: 'We work directly alongside your internal engineers. By sharing active technical knowledge and aligning architectural goals, we ensure everyday deployments run smoothly without friction.'
        },
        {
          title: 'Iterative Delivery',
          text: 'We deliver your infrastructure incrementally. This enables constant testing, rapid feedback loops, and quick, safe adjustments as your system requirements grow.'
        },
        {
          title: 'Long-Term Scalability',
          text: 'We build decoupled, clean architectures. Every platform we design is straightforward to test, cost-efficient to run, and ready to scale horizontally under heavy corporate workloads.'
        }
      ]
    },
    cta: {
      eyebrow: 'Execution',
      titleLine1: 'Ready to optimize',
      titleLine2: 'your data',
      titleHighlight: 'infrastructure',
      titleSuffix: '?',
      description:
        "Let's map out a plan to clean up your data platforms, remove manual mistakes, and optimize your technology investments."
    }
  },
  about: {
    hero: {
      eyebrow: 'Who we are',
      titleLine1: 'Data',
      titleHighlight: 'Architects',
      description:
        'We are a Data & AI firm specializing in modern data platforms, advanced analytics, scalable architectures, and high-performance engineering. We replace architectural complexity with resilient pipelines designed to scale under heavy workloads and continuous compliance.'
    },
    stats: {
      metricLabel: 'Performance Metric',
      items: [
        {
          value: '99.99%',
          label: 'Target Pipeline SLA & Production Uptime'
        },
        {
          value: '-40%',
          label: 'Average Infrastructure Cloud Cost Savings'
        },
        {
          value: '24/7',
          label: 'Continuous Automated Pipeline Monitoring'
        }
      ]
    },
    process: {
      eyebrow: '4D Framework',
      titleLine1: 'Our 4D',
      titleHighlight: 'Approach',
      description:
        'We move through four clear phases: Discover, Design, Develop, and Deliver. Each step reduces ambiguity, validates the platform direction, and keeps long-term management in view.',
      steps: [
        {
          number: '01',
          title: 'Discover',
          eyebrow: 'Understand the business',
          points: [
            'We map stakeholders, source systems, reporting pain points, and operational constraints so the work starts from business priorities rather than assumptions.',
            'We turn the findings into a ranked roadmap with clear risks, dependencies, and success metrics.'
          ]
        },
        {
          number: '02',
          title: 'Design',
          eyebrow: 'Shape the architecture',
          points: [
            'We define the target platform, data models, integration patterns, security boundaries, and ownership model.',
            'We choose technologies that fit the workload and design for scalability, observability, governance, and long-term maintenance.',
            'The result is a practical implementation blueprint that gives every delivery team a clear direction.'
          ]
        },
        {
          number: '03',
          title: 'Develop',
          eyebrow: 'Build and validate',
          points: [
            'We build pipelines, transformations, platform components, and automation in focused delivery cycles.',
            'Every release is tested, reviewed, documented, and connected to monitoring before it becomes part of production.',
            'We keep delivery transparent so teams can see progress early and adjust priorities without losing momentum.'
          ]
        },
        {
          number: '04',
          title: 'Deliver',
          eyebrow: 'Launch and improve',
          points: [
            'We launch the platform with runbooks, monitoring, access controls, and a structured handover.',
            'We train business users and engineering teams so the solution can be operated confidently after go-live.',
            'After delivery, we can stay involved through managed services, optimization, and further platform development.'
          ]
        }
      ]
    },
    techStackEyebrow: 'Core Ecosystem Expertise',
    locations: {
      eyebrow: 'Operations',
      title: 'Global Offices'
    }
  },
  partners: {
    hero: {
      eyebrow: 'Partner network',
      titleLine1: 'Our',
      titleHighlight: 'partners',
      description:
        'A focused network of infrastructure, cloud, ICT and software partners that helps us deliver dependable data systems across complex enterprise environments.'
    },
    stats: [
      {
        value: '03',
        label: 'Active partner profiles'
      },
      {
        value: '01',
        label: 'Reserved collaboration'
      },
      {
        value: '360°',
        label: 'Delivery coverage'
      }
    ],
    network: {
      title: 'Built around dependable execution.',
      description:
        'Each partner sits in a specific part of the delivery chain: infrastructure, managed platforms, ICT ecosystems or product engineering.'
    },
    lanes: [
      {
        title: 'Infrastructure depth',
        description:
          'Hosting, connectivity and platform operations for data workloads that need predictable production behavior.'
      },
      {
        title: 'Enterprise reach',
        description:
          'ICT ecosystems and regional delivery capacity for organizations with distributed technical environments.'
      },
      {
        title: 'Product engineering',
        description:
          'Software delivery capability that complements data platforms with reliable application layers.'
      }
    ],
    labels: {
      activeNetwork: 'Collaboration scope',
      activeNetworkDescription:
        'Combined coverage across hosting, enterprise ICT and software engineering.',
      partnerRole: 'Partner role',
      collaboration: 'Collaboration focus',
      region: 'Region',
      visitWebsite: 'Visit website'
    },
    items: {
      mainstream: {
        role: 'Sovereign cloud and hosting partner',
        focus:
          'DataSliv deployments, infrastructure operations and production-grade hosting paths.',
        region: 'Europe'
      },
      axians: {
        role: 'ICT and enterprise technology partner',
        focus:
          'Connectivity, cloud, data center and digital transformation ecosystems.',
        region: 'International'
      },
      ingsoftware: {
        role: 'Software engineering partner',
        focus:
          'Custom software delivery, product teams and application engineering for enterprise platforms.',
        region: 'Europe and global markets'
      },
      orion: {
        role: 'Telecom, cloud and data center partner',
        focus:
          'Connectivity, cloud infrastructure and business ICT services prepared for a future public partner profile.',
        region: 'Serbia'
      }
    }
  },
  contact: {
    hero: {
      eyebrow: 'Contact',
      titleStart: 'Get in',
      titleHighlight: 'contact',
      description:
        "Ready to build? Tell us about your data infrastructure or AI goals, and we'll map out a practical engineering solution."
    },
    form: {
      title: 'Send us a message',
      successMessage:
        'Thank you! Your message has been sent. We will get back to you soon.',
      successToast: 'Your message has been sent. We will get back to you soon.',
      notConfigured:
        'Contact form is not configured yet. Add VITE_GOOGLE_EMAILER_URL to your environment.',
      sendError:
        'We could not send your message right now. Please try again or email us directly.',
      honeypotLabel: 'Leave this field blank',
      labels: {
        firstName: 'First name *',
        lastName: 'Last name *',
        email: 'E-mail *',
        message: 'Message *'
      },
      placeholders: {
        firstName: 'Your first name',
        lastName: 'Your last name',
        email: 'you@email.com',
        message: 'Tell us how we can help...'
      },
      validation: {
        firstName: 'First name is required.',
        lastName: 'Last name is required.',
        email: 'E-mail is required.',
        message: 'Message is required.',
        invalidEmail: 'Please enter a valid email address.',
        fillRequired: 'Please fill in all required fields.'
      },
      sending: 'Sending...',
      send: 'Send'
    },
    directChannels: {
      title: 'Direct channels',
      description:
        'Reach the right team directly, or choose the office closest to you.',
      officesTitle: 'Offices',
      departments: [
        {
          title: 'Sales',
          description: 'For business inquiries and partnerships',
          email: 'sales@functor.com'
        },
        {
          title: 'Support',
          description: 'Technical support and customer service',
          email: 'support@functor.com'
        },
        {
          title: 'Careers',
          description: 'Join our growing team',
          email: 'careers@functor.com'
        }
      ]
    }
  },
  insights: {
    hero: {
      eyebrow: 'Insights',
      titleStart: 'Applied data',
      titleHighlight: 'thinking.',
      description:
        'Field notes, architecture lessons and practical viewpoints from the Functor team, connected back to the services and case studies behind the work.'
    },
    labels: {
      dateUnavailable: 'Date pending',
      fallbackTag: 'Functor Insight',
      minuteRead: (minutes: number) => `${minutes} min read`,
      openArticle: 'Open article',
      related: 'Related pages'
    },
    filters: {
      searchLabel: 'Search insights',
      searchPlaceholder: 'Search insights',
      topicLabel: 'Topic',
      allTopics: 'All topics',
      clear: 'Clear filters',
      results: (start: number, end: number, total: number) =>
        total ? `Showing ${start}-${end} of ${total}` : 'No matching insights',
      noResultsTitle: 'No insights match these filters.',
      noResultsDescription: 'Try another search or clear the filters.'
    },
    pagination: {
      previous: 'Previous',
      next: 'Next',
      page: (currentPage: number, totalPages: number) =>
        `Page ${currentPage} of ${totalPages}`
    },
    states: {
      notConfiguredTitle: 'Insights are almost ready.',
      notConfiguredDescription:
        'Published articles will appear here as soon as the blog feed is connected.',
      errorTitle: 'Insights could not load.',
      errorDescription:
        'The blog source did not respond. Try again in a moment.',
      retry: 'Reload feed',
      emptyTitle: 'No published insights yet.',
      emptyDescription:
        'Fresh articles will appear here once they are available from the blog.'
    },
    article: {
      backToInsights: 'Back to insights',
      errorTitle: 'Article could not load.',
      errorDescription:
        'The selected article is not available from the content API right now.',
      notFoundTitle: 'Article not found.',
      notFoundDescription:
        'This insight is not published for the current site or language.'
    },
    relatedLinks: {
      dataSliv: 'DataSliv platform',
      dataEngineering: 'Data Engineering',
      managedDataPlatform: 'Managed Data Platform',
      businessIntelligence: 'Business Intelligence',
      aiMachineLearning: 'AI & Machine Learning',
      cases: 'Case studies',
      contact: 'Contact'
    },
    cta: {
      eyebrow: 'Next step',
      title: 'Turn the ideas into a working roadmap.',
      description:
        'Pair the latest thinking with production experience from our delivery work.',
      cases: 'View cases',
      contact: 'Start a conversation'
    }
  },
  cases: {
    hero: {
      eyebrow: 'Case Studies',
      titleStart: 'Our',
      titleHighlight: 'Cases',
      description:
        'Proven sovereign implementations. We eliminate complex legacy system bottlenecks, execute large-scale cloud repatriation, and deliver robust compliance auditing.'
    },
    filters: {
      label: 'Filters',
      ariaLabel: 'Case study categories'
    },
    labels: {
      challenge: 'The Challenge',
      solution: 'The Solution',
      visitWebsite: 'Visit',
      ndaSecure: 'NDA SECURE'
    },
    categoryLabels: {
      All: 'All',
      Migration: 'Migration',
      'Data Engineering': 'Data Engineering',
      'Cloud Infrastructure': 'Cloud Infrastructure',
      'Streaming & AI': 'Streaming & AI',
      'Banking & Finance': 'Banking & Finance'
    },
    studies: {
      'cropt-satellite-ml-pipeline': {
        title: 'High-Volume Sat-Imagery Lakehouse for Cropt AgTech',
        challenge:
          'Processing petabytes of multi-spectral satellite imagery and geospatial data to support agricultural analytics and predictive ML models.',
        solution:
          'Built a scalable Delta Lakehouse platform that automated satellite data ingestion, transformation, and storage, enabling efficient crop monitoring, feature engineering, and machine learning workflows.',
        metrics: [
          { label: 'Training Speed', value: '15x' },
          { label: 'SLA Standard', value: '99.9%' }
        ],
        tags: [
          'AgTech Lakehouse',
          'Satellite Ingestion',
          'Model Accelerate',
          'GeoJSON Layers',
          'Delta Lake'
        ],
        placeholderLabel: ''
      },
      'design-software-dwh-analytics-optimization': {
        title:
          'Data Engineering and Analytics for a Global Design Software Company',
        challenge:
          'Unreliable, error-prone, non-idempotent data warehouse jobs were creating inconsistent reporting, slow SQL layers, multiple versions of truth, flawed outlier handling, and missing management KPIs.',
        solution:
          'Redesigned DWH jobs and reporting layers to be idempotent, accurate, and faster, introduced a single version of truth for metrics, automated reusable warehouse layers, applied sound statistical outlier handling, and rebuilt management reporting around the KPIs that matter.',
        metrics: [
          { label: 'DWH Jobs', value: 'Reliable' },
          { label: 'KPI Layer', value: 'Accurate' }
        ],
        tags: [
          'Design Software',
          'DWH Optimization',
          'KPI Analytics',
          'Single Version of Truth',
          'Outlier Methods'
        ],
        placeholderLabel: ''
      },
      'inspira-group-cloud-repatriation': {
        title: 'E-Commerce Cloud Repatriation and Cost Optimization',
        challenge:
          'Keeping analytics, reporting, and event processing stable across a growing portfolio of high-traffic digital products while controlling infrastructure spend and delivery risk.',
        solution:
          'Maintained and optimized the data platform around DataSliv, Kafka streams, and batch analytics so product teams could rely on fresher reporting and predictable infrastructure costs.',
        metrics: [
          { label: 'Cost Reduction', value: '18x' },
          { label: 'Workload Parity', value: '100%' }
        ],
        tags: [
          'Cloud Repatriation',
          'DataSliv',
          'E-Commerce BI',
          'Kafka Streams',
          'HDFS Storage'
        ],
        placeholderLabel: ''
      },
      'asml-data-modernization': {
        title:
          'Cloudera-to-Azure Migration for One of the Largest Semiconductor Companies',
        challenge:
          'Moving mission-critical industrial data workloads off an on-premises Cloudera estate while preserving reliability, lineage, and processing standards for complex engineering analytics.',
        solution:
          'Planned and executed the migration path to Azure and Databricks, redesigning core data layouts and Spark processing patterns for scalable cloud lakehouse operations.',
        metrics: [
          { label: 'Platform Shift', value: 'Azure' },
          { label: 'Lakehouse', value: 'Databricks' }
        ],
        tags: [
          'Cloudera Migration',
          'Microsoft Azure',
          'Databricks',
          'Semiconductor Data',
          'Lakehouse'
        ],
        placeholderLabel: 'CHIP INFRA'
      },
      'a1-serbia-dwh-migration': {
        title: 'Oracle-to-DataSliv Migration for a Large Balkan Telco Operator',
        challenge:
          'Modernizing an on-premises Oracle data warehouse environment where slow analytical queries, brittle ingestion workflows, and limited governance made daily reporting difficult to trust.',
        solution:
          'Migrated core ingestion, storage, analytics, and governance workloads from Oracle on-premises systems to a DataSliv deployment designed for telecom-scale operational data.',
        metrics: [
          { label: 'Query Speedup', value: '5x Fast' },
          { label: 'Data Sovereignty', value: '100%' }
        ],
        tags: [
          'DataSliv',
          'Oracle Migration',
          'Large Telco',
          'On-Premises',
          'Telecom Analytics'
        ],
        placeholderLabel: 'BALKAN TELCO'
      },
      'banca-intesa-compliance-lakehouse': {
        title:
          'Strict Regulatory Compliance Big Data Lake for a Large Bank With 2M+ Customers',
        challenge:
          'Architecting and scaling a massive core financial data lake deployed on GCP while adhering to the absolute strictest central banking regulatory standards.',
        solution:
          'Deployed expert data engineering patterns to enforce total pipeline transparency, multi-layered data auditing, and native NBS/EBA compliance frameworks.',
        metrics: [
          { label: 'Banking SLA', value: '99.99%' },
          { label: 'Regulatory Audit', value: 'Passed' }
        ],
        tags: [
          'Financial Data Lake',
          'GCP Architecture',
          'Banking Compliance',
          '2M+ Customers',
          'NBS / EBA',
          'Data Auditing'
        ],
        placeholderLabel: 'LARGE BANK'
      },
      'yettel-kickoff-consulting': {
        title: 'Data Lake Maintenance for a Large Telco',
        challenge:
          'Keeping a high-volume telecom data lake stable, observable, and ready for daily analytics workloads as ingestion volume and downstream reporting demands increased.',
        solution:
          'Provided ongoing data lake maintenance, pipeline hardening, performance tuning, and operational support so internal teams could rely on predictable, fresh datasets.',
        metrics: [
          { label: 'Data Lake', value: 'Maintained' },
          { label: 'Pipeline Ops', value: 'Stable' }
        ],
        tags: [
          'Large Telco',
          'Data Lake Maintenance',
          'Pipeline Operations',
          'Performance Tuning',
          'Operational Support'
        ],
        placeholderLabel: 'ENTERPRISE DWH'
      }
    }
  },
  services: {
    detail: {
      backToHome: 'Back to home',
      eyebrow: 'Service',
      bookIntroduction: 'Book an introduction',
      viewCases: 'View cases',
      whatWeDeliver: 'What we deliver',
      deliverTitle: 'Stable systems, not experiments.',
      capabilities: 'Capabilities',
      capabilitiesTitle:
        'Engineering blocks that make your data platform dependable.',
      included: 'Included',
      defaultCtaTag: 'Execution',
      defaultCtaTitle: 'Ready to modernize your data infrastructure?',
      defaultCtaDescription:
        'We can review your current setup, identify the biggest bottlenecks, and map out a practical plan for reliable pipelines, cleaner storage, and lower operational costs.',
      defaultCtaButton: 'Start a project'
    },
    pages: {
      dataEngineering: {
        title: 'Data',
        highlightedText: 'Engineering',
        description:
          'We design and build modern, stable data platforms that help your business handle large volumes of information without crashes, delays, or high cloud costs.',
        points: [
          {
            title: 'Reliable Data Pipelines',
            description:
              'We build automated pipelines that move data smoothly between your systems, cloud storage, and databases without any data loss or duplication.'
          },
          {
            title: 'Modern Data Warehouses & Lakehouses',
            description:
              'We organize your company storage using modern cloud standards. Your data stays clean, easy to access, and cheap to run, even under heavy usage.'
          },
          {
            title: 'Automated Data Quality Testing',
            description:
              'Our automated systems constantly check your data for errors or unexpected changes, making sure your analytics and reports are always 100% accurate.'
          },
          {
            title: 'High-Performance Data Processing',
            description:
              'Using leading industry tools like Apache Spark and Scala, we build processing systems that quickly clean and aggregate massive datasets within your deadlines.'
          },
          {
            title: 'Enterprise Security & Governance',
            description:
              'We secure your infrastructure with strict encryption and automated access controls, ensuring your customer data is completely safe and follows all regulations.'
          }
        ]
      },
      managedDataPlatform: {
        title: 'Managed Data',
        highlightedText: 'Platform',
        description:
          'Keep your data infrastructure production-ready 24/7. We take care of continuous monitoring, scaling, and platform maintenance so your team can focus strictly on driving business value.',
        ctaTag: 'Platform Evaluation',
        ctaTitle: 'Ready to secure your data platform uptime?',
        ctaDescription:
          "Let's audit your current cloud setup, eliminate hidden infrastructure bottlenecks, and build a reliable, zero-overhead management strategy together.",
        ctaButtonText: 'Request Platform Audit',
        points: [
          {
            title: 'Cloud Infrastructure & Setup',
            description:
              'We deploy and structure secure, isolated cloud environments tailored to your company needs, using modern industry security standards.'
          },
          {
            title: '24/7 Monitoring & Alerting',
            description:
              'Our systems constantly monitor your pipelines to catch and fix unexpected errors automatically, ensuring your business data never stops moving.'
          },
          {
            title: 'Scalable Foundation for Future Tech',
            description:
              'We keep your data platforms flexible and ready to instantly connect with new dashboards, data science workflows, or AI tools whenever you need them.'
          },
          {
            title: 'Full Platform Maintenance',
            description:
              'We take full technical ownership of infrastructure updates, database patches, and system migrations, freeing your team from maintenance overhead.'
          },
          {
            title: 'Cloud Cost Optimization',
            description:
              'We run regular cluster audits, set up automatic sleep schedules for unused resources, and manage storage tiers to continuously lower your monthly cloud bills.'
          }
        ]
      },
      businessIntelligence: {
        title: 'Business',
        highlightedText: 'Intelligence',
        description:
          'Transform your database numbers into clean, visual dashboards and automated reports that help your leadership team make fast, confident business decisions.',
        ctaTag: 'Analytics Evaluation',
        ctaTitle: 'Ready to turn raw data into clear business metrics?',
        ctaDescription:
          "Let's review your current data models, clean up your reporting layers, and build interactive dashboards that drive smarter management decisions.",
        ctaButtonText: 'Optimize Analytics',
        points: [
          {
            title: 'Executive Dashboards & KPI Tracking',
            description:
              'We design high-impact, interactive financial and operational dashboards built directly around the primary metrics that drive your business growth.'
          },
          {
            title: 'Single Source of Truth',
            description:
              'We build centralized data models using modern tools like dbt, ensuring all departments look at the exact same numbers and eliminating conflicting reports.'
          },
          {
            title: 'Secure Self-Service Analytics',
            description:
              'We set up controlled access layers that allow your team leaders to safely explore validated data and build their own reports without breaking anything.'
          },
          {
            title: 'Automated Slack & Teams Alerts',
            description:
              'We connect your reporting layers directly to your daily communication tools, triggering automatic alerts and insights exactly where your team collaborates.'
          },
          {
            title: 'Team Training & Platform Adoption',
            description:
              "We don't just build dashboards; we provide clear documentation and hands-on training to ensure your team turns data into a daily operational habit."
          }
        ]
      },
      aiMachineLearning: {
        title: 'AI & Machine',
        highlightedText: 'Learning',
        description:
          'Apply artificial intelligence and machine learning where it creates measurable business value, from intelligent workflow automation to predictive models.',
        ctaTag: 'AI Strategy Assessment',
        ctaTitle: 'Ready to scale your operations with custom AI models?',
        ctaDescription:
          "Let's identify automation opportunities, evaluate your training datasets, and deploy production-grade AI solutions tailored to your business logic.",
        ctaButtonText: 'Deploy AI Solutions',
        points: [
          {
            title: 'Practical AI Use-Case Discovery',
            description:
              'We run a technical assessment of your operations to separate realistic, high-value AI integrations from speculative market noise and hype.'
          },
          {
            title: 'Predictive & Forecasting Models',
            description:
              'We build machine learning models that analyze historical data to forecast trends, predict customer behavior, and integrate directly with your cloud databases.'
          },
          {
            title: 'Intelligent Workflow Automation',
            description:
              'We design end-to-end automation systems that eliminate manual bottlenecks, handling complex, data-heavy tasks automatically and at high speed.'
          },
          {
            title: 'Secure AI Control & Guardrails',
            description:
              'We build strict security layers and privacy guardrails around your models, ensuring your AI behaves predictably and protects sensitive company data.'
          },
          {
            title: 'Continuous Model Optimization',
            description:
              'We set up automated systems that constantly update and check your AI models, ensuring they stay 100% accurate and efficient as your business data evolves.'
          }
        ]
      }
    }
  },
  notFound: {
    badge: '404 route',
    title: 'This page is not in the pipeline.',
    description:
      'The address does not match an active Functor page. Jump back to a reliable route or continue into the sections people use most.',
    home: 'Home',
    back: 'Back',
    contact: 'Contact',
    redirect: 'Redirect',
    redirectDescription:
      'Pick a stable destination and keep moving through the site.',
    quickLinks: [
      { label: 'Data Engineering', path: '/services/data-engineering' },
      {
        label: 'Managed Data Platform',
        path: '/services/managed-data-platform'
      },
      { label: 'Case Studies', path: '/cases' },
      { label: 'Contact', path: '/contact' }
    ]
  },
  errorBoundary: {
    title: 'Oops! Something went wrong',
    description:
      "We encountered an unexpected error. Don't worry, it's not your fault.",
    details: 'Error Details (Dev Mode)',
    tryAgain: 'Try Again',
    reloadPage: 'Reload Page',
    goHome: 'Go Home'
  }
};

export type Translation = typeof en;

const sr: Translation = {
  common: {
    brand: 'Functor',
    close: 'Zatvori',
    languageNames: {
      en: 'Engleski',
      sr: 'Srpski',
      es: 'Španski'
    },
    languageShortNames: {
      en: 'EN',
      sr: 'SR',
      es: 'ES'
    },
    countries: {
      serbia: 'Srbija',
      unitedStates: 'Sjedinjene Američke Države',
      croatia: 'Hrvatska'
    },
    nav: {
      ariaLabel: 'Glavna navigacija',
      homeAriaLabel: 'Functor početna',
      home: 'Početna',
      services: 'Usluge',
      cases: 'Projekti',
      insights: 'Uvidi',
      about: 'O nama',
      partners: 'Partneri',
      contact: 'Kontakt',
      changeTheme: 'Promeni temu',
      openMenu: 'Otvori meni',
      languageSelector: 'Izaberi jezik',
      darkMode: 'Tamni režim',
      lightMode: 'Svetli režim'
    },
    serviceMenu: [
      {
        label: 'On-prem Datasliv platforma',
        description: 'Pipeline-ovi, skladištenje podataka i kvalitet podataka.'
      },
      {
        label: 'Data inženjering',
        description: 'Pipeline-ovi, skladištenje podataka i kvalitet podataka.'
      },
      {
        label: 'Managed data platforma',
        description: 'Operacije i podrška za cloud platforme.'
      },
      {
        label: 'Business Intelligence',
        description: 'Dashboardi, KPI-jevi i self-service analitika.'
      },
      {
        label: 'AI i machine learning',
        description: 'Predikcija, automatizacija i odgovoran AI.'
      }
    ],
    buttons: {
      bookIntroduction: 'Zakaži uvodni razgovor',
      discoverExpertise: 'Pogledaj ekspertizu',
      viewFullCaseStudy: 'Pogledaj celu studiju',
      browseAllCaseStudies: 'Pogledaj sve projekte',
      seeEngineeringStandards: 'Pogledaj naše inženjerske standarde',
      workWithUs: 'Radite sa nama'
    },
    techPartners: [
      'Databricks',
      'Apache Spark',
      'Microsoft Azure',
      'AWS',
      'Snowflake',
      'Scala'
    ]
  },
  pageTitles: {
    home: 'Functor | AI, data i analitički konsalting',
    dataEngineering: 'Data inženjering | Functor',
    managedDataPlatform: 'Managed data platforma | Functor',
    businessIntelligence: 'Business Intelligence | Functor',
    aiMachineLearning: 'AI i machine learning | Functor',
    cases: 'Projekti | Functor',
    insights: 'Insights | Functor',
    about: 'O nama | Functor',
    partners: 'Partneri | Functor',
    contact: 'Kontakt | Functor',
    notFound: '404 Stranica nije pronađena | Functor',
    error: 'Došlo je do greške | Functor'
  },
  footer: {
    description:
      'Functor je Data & AI firma specijalizovana za moderne data platforme, naprednu analitiku i visokoperformansni inženjering.',
    contactUs: 'Kontaktirajte nas',
    allRightsReserved: 'Sva prava zadržana.'
  },
  backToTop: {
    ariaLabel: (progress: number) =>
      `Vrati na vrh stranice. Napredak stranice ${progress}%.`
  },
  home: {
    hero: {
      eyebrow: 'Moderna data i enterprise AI infrastruktura',
      titleLine1: 'Gradimo moderna data',
      titleLine2: 'i enterprise AI',
      titleHighlight: 'rešenja.',
      description:
        'Functor projektuje, gradi i održava moderne data platforme i enterprise AI sisteme koji vam daju potpunu kontrolu nad infrastrukturom. Neuređena okruženja pretvaramo u čiste, pouzdane automatizovane pipeline-ove.',
      capabilitiesAria: 'Idi na sekciju usluga'
    },
    services: {
      eyebrow: 'Usluge',
      title: 'Arhitektura ključnih sistema.',
      items: [
        {
          title: 'Data inženjering',
          desc: 'Automatizujemo tokove podataka, sređujemo baze i gradimo stabilne pipeline-ove da timovi uvek rade sa tačnim brojevima.'
        },
        {
          title: 'Managed platforma',
          desc: 'Kontinuirani monitoring, optimizacija servera i kompletno održavanje sistema kako bi podaci uvek nastavili da teku.'
        },
        {
          title: 'Business Intelligence',
          desc: 'Pretvaramo sirove podatke u jasne vizuelne dashboarde koji usklađuju timove i pomažu menadžmentu da donosi brze odluke.'
        },
        {
          title: 'AI i machine learning',
          desc: 'Gradimo pametnu automatizaciju, modele za predviđanje trendova i asistentske procese koji stvaraju konkretnu poslovnu vrednost.'
        }
      ]
    },
    cases: {
      titleStart: 'Dokazani',
      titleHighlight: 'rezultati.',
      description:
        'Gradimo čistu data infrastrukturu koja rešava stvarna operativna uska grla. Pogledajte kako se naš inženjering direktno pretvara u niže troškove infrastrukture, brže sisteme i pouzdane poslovne odluke.'
    },
    approach: {
      eyebrow: 'Naši ključni principi',
      titleStart: 'Inženjerski procesi fokusirani na',
      titleHighlight: 'dugoročnu stabilnost.',
      items: [
        {
          title: 'Pouzdane codebase osnove',
          text: 'Oslanjamo se na čiste funkcionalne obrasce da uklonimo skrivene sporedne efekte. Tako vaši data pipeline-ovi ostaju predvidljivi i otporni na produkcione greške.'
        },
        {
          title: 'Integracija sa timom',
          text: 'Radimo direktno uz vaše interne inženjere. Deljenjem aktivnog tehničkog znanja i usklađivanjem arhitektonskih ciljeva omogućavamo stabilne svakodnevne isporuke.'
        },
        {
          title: 'Iterativna isporuka',
          text: 'Infrastrukturu isporučujemo postepeno. To omogućava stalno testiranje, brze povratne informacije i precizna prilagođavanja dok zahtevi rastu.'
        },
        {
          title: 'Dugoročna skalabilnost',
          text: 'Gradimo čiste i odvojene arhitekture. Svaka platforma koju dizajniramo laka je za testiranje, ekonomična za rad i spremna za horizontalno skaliranje.'
        }
      ]
    },
    cta: {
      eyebrow: 'Izvedba',
      titleLine1: 'Spremni da optimizujete',
      titleLine2: 'svoju data',
      titleHighlight: 'infrastrukturu',
      titleSuffix: '?',
      description:
        'Hajde da napravimo plan za sređivanje vaših data platformi, uklanjanje ručnih grešaka i bolju iskorišćenost tehnoloških investicija.'
    }
  },
  about: {
    hero: {
      eyebrow: 'Ko smo mi',
      titleLine1: 'Data',
      titleHighlight: 'arhitekte',
      description:
        'Mi smo Data & AI firma specijalizovana za moderne data platforme, naprednu analitiku, skalabilne arhitekture i visokoperformansni inženjering. Arhitektonsku složenost zamenjujemo otpornim pipeline-ovima projektovanim za velika opterećenja i stalnu usklađenost.'
    },
    stats: {
      metricLabel: 'Performansna metrika',
      items: [
        {
          value: '99.99%',
          label: 'Ciljani SLA pipeline-ova i produkcioni uptime'
        },
        {
          value: '-40%',
          label: 'Prosečna ušteda cloud infrastrukturnih troškova'
        },
        {
          value: '24/7',
          label: 'Kontinuirani automatizovani monitoring pipeline-ova'
        }
      ]
    },
    process: {
      eyebrow: '4D okvir',
      titleLine1: 'Naš 4D',
      titleHighlight: 'pristup',
      description:
        'Krećemo se kroz četiri jasne faze: Discover, Design, Develop i Deliver. Svaki korak smanjuje nejasnoće, potvrđuje smer platforme i čuva dugoročno upravljanje u fokusu.',
      steps: [
        {
          number: '01',
          title: 'Discover',
          eyebrow: 'Razumevanje biznisa',
          points: [
            'Mapiramo stejkholdere, izvorne sisteme, bolne tačke izveštavanja i operativna ograničenja kako bi posao krenuo od poslovnih prioriteta, a ne od pretpostavki.',
            'Nalaze pretvaramo u rangiran roadmap sa jasnim rizicima, zavisnostima i merilima uspeha.'
          ]
        },
        {
          number: '02',
          title: 'Design',
          eyebrow: 'Oblikovanje arhitekture',
          points: [
            'Definišemo ciljnu platformu, data modele, integracione obrasce, bezbednosne granice i model vlasništva.',
            'Biramo tehnologije koje odgovaraju workload-u i dizajniramo za skalabilnost, observability, governance i dugoročno održavanje.',
            'Rezultat je praktičan implementacioni blueprint koji svakom delivery timu daje jasan pravac.'
          ]
        },
        {
          number: '03',
          title: 'Develop',
          eyebrow: 'Izgradnja i validacija',
          points: [
            'Gradimo pipeline-ove, transformacije, platformne komponente i automatizaciju kroz fokusirane cikluse isporuke.',
            'Svako izdanje se testira, pregleda, dokumentuje i povezuje sa monitoringom pre ulaska u produkciju.',
            'Isporuku držimo transparentnom kako bi timovi rano videli napredak i prilagodili prioritete bez gubitka tempa.'
          ]
        },
        {
          number: '04',
          title: 'Deliver',
          eyebrow: 'Lansiranje i unapređenje',
          points: [
            'Platformu lansiramo uz runbook-ove, monitoring, kontrole pristupa i strukturiranu primopredaju.',
            'Obučavamo poslovne korisnike i inženjerske timove da rešenje mogu samostalno i sigurno da koriste posle go-live faze.',
            'Nakon isporuke možemo ostati uključeni kroz managed services, optimizaciju i dalji razvoj platforme.'
          ]
        }
      ]
    },
    techStackEyebrow: 'Ekspertiza u ključnom ekosistemu',
    locations: {
      eyebrow: 'Operacije',
      title: 'Globalne kancelarije'
    }
  },
  partners: {
    hero: {
      eyebrow: 'Mreža partnera',
      titleLine1: 'Naši',
      titleHighlight: 'partneri',
      description:
        'Fokusirana mreža infrastrukturnih, cloud, ICT i softverskih partnera koja nam pomaže da isporučujemo pouzdane data sisteme u kompleksnim enterprise okruženjima.'
    },
    stats: [
      {
        value: '03',
        label: 'Aktivna profila partnera'
      },
      {
        value: '01',
        label: 'Rezervisana saradnja'
      },
      {
        value: '360°',
        label: 'Pokrivenost isporuke'
      }
    ],
    network: {
      title: 'Izgrađeno oko pouzdane realizacije.',
      description:
        'Svaki partner zauzima konkretan deo delivery lanca: infrastrukturu, managed platforme, ICT ekosisteme ili product engineering.'
    },
    lanes: [
      {
        title: 'Infrastrukturna dubina',
        description:
          'Hosting, konektivnost i platform operations za data workload-e kojima je potrebno predvidljivo produkciono ponašanje.'
      },
      {
        title: 'Enterprise domet',
        description:
          'ICT ekosistemi i regionalni delivery kapacitet za organizacije sa distribuiranim tehničkim okruženjima.'
      },
      {
        title: 'Product engineering',
        description:
          'Softverski delivery kapacitet koji dopunjuje data platforme pouzdanim aplikativnim slojevima.'
      }
    ],
    labels: {
      activeNetwork: 'Obim saradnje',
      activeNetworkDescription:
        'Zajednička pokrivenost hostinga, enterprise ICT-a i softverskog inženjeringa.',
      partnerRole: 'Uloga partnera',
      collaboration: 'Fokus saradnje',
      region: 'Region',
      visitWebsite: 'Poseti sajt'
    },
    items: {
      mainstream: {
        role: 'Partner za suvereni cloud i hosting',
        focus:
          'DataSliv deployment-i, infrastrukturne operacije i produkciono spremni hosting tokovi.',
        region: 'Evropa'
      },
      axians: {
        role: 'ICT i enterprise technology partner',
        focus:
          'Konektivnost, cloud, data centar i ekosistemi digitalne transformacije.',
        region: 'Internacionalno'
      },
      ingsoftware: {
        role: 'Partner za softverski inženjering',
        focus:
          'Custom software delivery, produktni timovi i aplikativni inženjering za enterprise platforme.',
        region: 'Evropa i globalna tržišta'
      },
      orion: {
        role: 'Telekom, cloud i data centar partner',
        focus:
          'Konektivnost, cloud infrastruktura i poslovni ICT servisi pripremljeni za budući javni profil partnera.',
        region: 'Srbija'
      }
    }
  },
  contact: {
    hero: {
      eyebrow: 'Kontakt',
      titleStart: 'Stupite u',
      titleHighlight: 'kontakt',
      description:
        'Spremni ste za izgradnju? Recite nam nešto o svojoj data infrastrukturi ili AI ciljevima, a mi ćemo predložiti praktično inženjersko rešenje.'
    },
    form: {
      title: 'Pošaljite nam poruku',
      successMessage: 'Hvala! Vaša poruka je poslata. Javićemo vam se uskoro.',
      successToast: 'Vaša poruka je poslata. Javićemo vam se uskoro.',
      notConfigured:
        'Kontakt forma još nije podešena. Dodajte VITE_GOOGLE_EMAILER_URL u environment.',
      sendError:
        'Trenutno ne možemo da pošaljemo poruku. Pokušajte ponovo ili nam pišite direktno.',
      honeypotLabel: 'Ostavite ovo polje prazno',
      labels: {
        firstName: 'Ime *',
        lastName: 'Prezime *',
        email: 'E-mail *',
        message: 'Poruka *'
      },
      placeholders: {
        firstName: 'Vaše ime',
        lastName: 'Vaše prezime',
        email: 'vi@email.com',
        message: 'Recite nam kako možemo da pomognemo...'
      },
      validation: {
        firstName: 'Ime je obavezno.',
        lastName: 'Prezime je obavezno.',
        email: 'E-mail je obavezan.',
        message: 'Poruka je obavezna.',
        invalidEmail: 'Unesite ispravnu e-mail adresu.',
        fillRequired: 'Popunite sva obavezna polja.'
      },
      sending: 'Slanje...',
      send: 'Pošalji'
    },
    directChannels: {
      title: 'Direktni kanali',
      description:
        'Obratite se pravom timu direktno ili izaberite kancelariju koja vam je najbliža.',
      officesTitle: 'Kancelarije',
      departments: [
        {
          title: 'Prodaja',
          description: 'Za poslovne upite i partnerstva',
          email: 'sales@functor.com'
        },
        {
          title: 'Podrška',
          description: 'Tehnička podrška i korisnička služba',
          email: 'support@functor.com'
        },
        {
          title: 'Karijera',
          description: 'Pridružite se našem timu',
          email: 'careers@functor.com'
        }
      ]
    }
  },
  insights: {
    hero: {
      eyebrow: 'Insights',
      titleStart: 'Praktično data',
      titleHighlight: 'razmišljanje.',
      description:
        'Beleške iz prakse, arhitektonske lekcije i konkretni stavovi Functor tima, povezani sa uslugama i projektima koji stoje iza rada.'
    },
    labels: {
      dateUnavailable: 'Datum u pripremi',
      fallbackTag: 'Functor Insight',
      minuteRead: (minutes: number) => `${minutes} min čitanja`,
      openArticle: 'Otvori objavu',
      related: 'Povezane stranice'
    },
    filters: {
      searchLabel: 'Pretraga objava',
      searchPlaceholder: 'Pretraži objave',
      topicLabel: 'Tema',
      allTopics: 'Sve teme',
      clear: 'Poništi filtere',
      results: (start: number, end: number, total: number) =>
        total ? `Prikaz ${start}-${end} od ${total}` : 'Nema rezultata',
      noResultsTitle: 'Nema objava za ove filtere.',
      noResultsDescription: 'Probajte drugu pretragu ili poništite filtere.'
    },
    pagination: {
      previous: 'Prethodna',
      next: 'Sledeća',
      page: (currentPage: number, totalPages: number) =>
        `Strana ${currentPage} od ${totalPages}`
    },
    states: {
      notConfiguredTitle: 'Insights je skoro spreman.',
      notConfiguredDescription:
        'Objave će se pojaviti ovde čim se blog feed poveže.',
      errorTitle: 'Insights trenutno ne može da se učita.',
      errorDescription:
        'Blog izvor nije odgovorio. Pokušajte ponovo za koji trenutak.',
      retry: 'Učitaj ponovo',
      emptyTitle: 'Još nema objavljenih insight-a.',
      emptyDescription:
        'Nove objave će se pojaviti ovde čim budu dostupne na blogu.'
    },
    article: {
      backToInsights: 'Nazad na Insights',
      errorTitle: 'Objava trenutno ne može da se učita.',
      errorDescription:
        'Izabrana objava trenutno nije dostupna iz content API-ja.',
      notFoundTitle: 'Objava nije pronađena.',
      notFoundDescription:
        'Ovaj insight nije objavljen za trenutni sajt ili izabrani jezik.'
    },
    relatedLinks: {
      dataSliv: 'DataSliv platforma',
      dataEngineering: 'Data inženjering',
      managedDataPlatform: 'Managed Data Platform',
      businessIntelligence: 'Business Intelligence',
      aiMachineLearning: 'AI i machine learning',
      cases: 'Projekti',
      contact: 'Kontakt'
    },
    cta: {
      eyebrow: 'Sledeći korak',
      title: 'Pretvorite ideje u konkretan roadmap.',
      description:
        'Povežite najnovije uvide sa produkcionim iskustvom iz naših delivery projekata.',
      cases: 'Pogledaj projekte',
      contact: 'Započni razgovor'
    }
  },
  cases: {
    hero: {
      eyebrow: 'Studije slučaja',
      titleStart: 'Naši',
      titleHighlight: 'projekti',
      description:
        'Dokazane suverene implementacije. Uklanjamo uska grla kompleksnih legacy sistema, izvodimo cloud repatriation velikog obima i isporučujemo robusnu compliance audit infrastrukturu.'
    },
    filters: {
      label: 'Filteri',
      ariaLabel: 'Kategorije studija slučaja'
    },
    labels: {
      challenge: 'Izazov',
      solution: 'Rešenje',
      visitWebsite: 'Poseti',
      ndaSecure: 'NDA SIGURNO'
    },
    categoryLabels: {
      All: 'Svi',
      Migration: 'Migracije',
      'Data Engineering': 'Data inženjering',
      'Cloud Infrastructure': 'Cloud infrastruktura',
      'Streaming & AI': 'Streaming i AI',
      'Banking & Finance': 'Bankarstvo i finansije'
    },
    studies: {
      'cropt-satellite-ml-pipeline': {
        title: 'Lakehouse za satelitske snimke velikog obima za Cropt AgTech',
        challenge:
          'Obrada petabajta multispektralnih satelitskih snimaka i geospatial podataka za podršku agrarnoj analitici i prediktivnim ML modelima.',
        solution:
          'Izgradili smo skalabilnu Delta Lakehouse platformu koja automatizuje unos, transformaciju i skladištenje satelitskih podataka, uz efikasno praćenje useva, feature engineering i machine learning tokove.',
        metrics: [
          { label: 'Brzina treniranja', value: '15x' },
          { label: 'SLA standard', value: '99.9%' }
        ],
        tags: [
          'AgTech Lakehouse',
          'Unos satelitskih podataka',
          'Ubrzanje modela',
          'GeoJSON slojevi',
          'Delta Lake'
        ],
        placeholderLabel: ''
      },
      'design-software-dwh-analytics-optimization': {
        title:
          'Data inženjering i analitika za globalnu kompaniju za dizajn softver',
        challenge:
          'Nepouzdani, greškama skloni i ne-idempotentni data warehouse poslovi stvarali su nedosledno izveštavanje, spore SQL slojeve, više verzija istine, loše rukovanje outlier-ima i nedostajuće menadžment KPI-jeve.',
        solution:
          'Redizajnirali smo DWH poslove i reporting slojeve da budu idempotentni, tačni i brži, uveli jednu verziju istine za metrike, automatizovali reusable warehouse slojeve, primenili ispravne statističke metode za outlier-e i obnovili menadžment reporting oko ključnih KPI-jeva.',
        metrics: [
          { label: 'DWH poslovi', value: 'Pouzdani' },
          { label: 'KPI sloj', value: 'Tačan' }
        ],
        tags: [
          'Dizajn softver',
          'DWH optimizacija',
          'KPI analitika',
          'Jedna verzija istine',
          'Outlier metode'
        ],
        placeholderLabel: ''
      },
      'inspira-group-cloud-repatriation': {
        title: 'E-commerce cloud repatriation i optimizacija troškova',
        challenge:
          'Održavanje stabilne analitike, izveštavanja i obrade događaja kroz rastući portfolio digitalnih proizvoda sa visokim saobraćajem, uz kontrolu infrastrukturnih troškova i delivery rizika.',
        solution:
          'Održavali smo i optimizovali data platformu oko DataSliv-a, Kafka stream-ova i batch analitike kako bi produktni timovi imali svežije izveštaje i predvidljive infrastrukturne troškove.',
        metrics: [
          { label: 'Smanjenje troškova', value: '18x' },
          { label: 'Paritet workload-a', value: '100%' }
        ],
        tags: [
          'Cloud repatriation',
          'DataSliv',
          'E-commerce BI',
          'Kafka stream-ovi',
          'HDFS skladište'
        ],
        placeholderLabel: ''
      },
      'asml-data-modernization': {
        title:
          'Migracija sa Cloudera platforme na Azure za jednu od najvećih kompanija u industriji poluprovodnika',
        challenge:
          'Premeštanje mission-critical industrijskih data workload-a sa on-premises Cloudera okruženja uz očuvanje pouzdanosti, lineage-a i procesnih standarda za kompleksnu inženjersku analitiku.',
        solution:
          'Isplanirali smo i izveli migracioni put ka Azure-u i Databricks-u, redizajnirajući ključne data layoute i Spark obrasce obrade za skalabilne cloud lakehouse operacije.',
        metrics: [
          { label: 'Promena platforme', value: 'Azure' },
          { label: 'Lakehouse', value: 'Databricks' }
        ],
        tags: [
          'Cloudera migracija',
          'Microsoft Azure',
          'Databricks',
          'Semiconductor data',
          'Lakehouse'
        ],
        placeholderLabel: 'CHIP INFRA'
      },
      'a1-serbia-dwh-migration': {
        title:
          'Oracle-to-DataSliv migracija za velikog balkanskog telco operatora',
        challenge:
          'Modernizacija on-premises Oracle data warehouse okruženja u kom su spori analitički upiti, krhki ingestion tokovi i ograničen governance otežavali poverenje u dnevno izveštavanje.',
        solution:
          'Migrirali smo ključne ingestion, storage, analytics i governance workload-e sa Oracle on-premises sistema na DataSliv deployment projektovan za operativne podatke telco obima.',
        metrics: [
          { label: 'Ubrzanje upita', value: '5x brže' },
          { label: 'Suverenost podataka', value: '100%' }
        ],
        tags: [
          'DataSliv',
          'Oracle migracija',
          'Veliki telco',
          'On-premises',
          'Telco analitika'
        ],
        placeholderLabel: 'BALKAN TELCO'
      },
      'banca-intesa-compliance-lakehouse': {
        title:
          'Big data lake za strogu regulatornu usklađenost velike banke sa 2M+ klijenata',
        challenge:
          'Arhitektura i skaliranje masivnog core finansijskog data lake-a na GCP-u uz poštovanje najstrožih standarda centralnog bankarskog regulatora.',
        solution:
          'Primena ekspertnih data engineering obrazaca za potpunu transparentnost pipeline-ova, višeslojni data audit i native NBS/EBA compliance okvire.',
        metrics: [
          { label: 'Bankarski SLA', value: '99.99%' },
          { label: 'Regulatorni audit', value: 'Prošao' }
        ],
        tags: [
          'Finansijski data lake',
          'GCP arhitektura',
          'Bankarski compliance',
          '2M+ klijenata',
          'NBS / EBA',
          'Data auditing'
        ],
        placeholderLabel: 'VELIKA BANKA'
      },
      'yettel-kickoff-consulting': {
        title: 'Održavanje data lake-a za veliki telco',
        challenge:
          'Održavanje stabilnog, observable i za dnevnu analitiku spremnog telecom data lake-a dok rastu ingestion volumen i downstream reporting zahtevi.',
        solution:
          'Pružili smo kontinuirano održavanje data lake-a, ojačavanje pipeline-ova, tuning performansi i operativnu podršku kako bi interni timovi mogli da računaju na predvidljive i sveže datasete.',
        metrics: [
          { label: 'Data lake', value: 'Održavan' },
          { label: 'Pipeline operacije', value: 'Stabilne' }
        ],
        tags: [
          'Veliki telco',
          'Održavanje data lake-a',
          'Pipeline operacije',
          'Tuning performansi',
          'Operativna podrška'
        ],
        placeholderLabel: 'ENTERPRISE DWH'
      }
    }
  },
  services: {
    detail: {
      backToHome: 'Nazad na početnu',
      eyebrow: 'Usluga',
      bookIntroduction: 'Zakaži uvodni razgovor',
      viewCases: 'Pogledaj projekte',
      whatWeDeliver: 'Šta isporučujemo',
      deliverTitle: 'Stabilni sistemi, ne eksperimenti.',
      capabilities: 'Mogućnosti',
      capabilitiesTitle:
        'Inženjerski blokovi koji vašu data platformu čine pouzdanom.',
      included: 'Uključeno',
      defaultCtaTag: 'Izvedba',
      defaultCtaTitle: 'Spremni da modernizujete data infrastrukturu?',
      defaultCtaDescription:
        'Možemo pregledati vaš trenutni setup, pronaći najveća uska grla i napraviti praktičan plan za pouzdane pipeline-ove, čistije skladište i niže operativne troškove.',
      defaultCtaButton: 'Pokreni projekat'
    },
    pages: {
      dataEngineering: {
        title: 'Data',
        highlightedText: 'Engineering',
        description:
          'Projektujemo i gradimo moderne, stabilne data platforme koje vašem biznisu pomažu da obradi velike količine informacija bez padova, kašnjenja i visokih cloud troškova.',
        points: [
          {
            title: 'Pouzdani data pipeline-ovi',
            description:
              'Gradimo automatizovane pipeline-ove koji glatko premeštaju podatke između vaših sistema, cloud storage-a i baza bez gubitka ili dupliranja podataka.'
          },
          {
            title: 'Moderni data warehouse-i i lakehouse-i',
            description:
              'Organizujemo skladište kompanije po modernim cloud standardima. Podaci ostaju čisti, lako dostupni i ekonomični za rad, čak i pod velikim opterećenjem.'
          },
          {
            title: 'Automatizovano testiranje kvaliteta podataka',
            description:
              'Naši automatizovani sistemi stalno proveravaju podatke na greške ili neočekivane promene, tako da su analitika i izveštaji uvek tačni.'
          },
          {
            title: 'Visokoperformansna obrada podataka',
            description:
              'Koristeći alate poput Apache Spark-a i Scale, gradimo sisteme koji brzo čiste i agregiraju masivne datasete u okviru vaših rokova.'
          },
          {
            title: 'Enterprise bezbednost i governance',
            description:
              'Infrastrukturu štitimo enkripcijom i automatizovanim kontrolama pristupa, čuvajući korisničke podatke i usklađenost sa regulativom.'
          }
        ]
      },
      managedDataPlatform: {
        title: 'Managed data',
        highlightedText: 'Platforma',
        description:
          'Držite data infrastrukturu produkciono spremnom 24/7. Mi preuzimamo monitoring, skaliranje i održavanje platforme kako bi se vaš tim fokusirao na poslovnu vrednost.',
        ctaTag: 'Procena platforme',
        ctaTitle: 'Spremni da obezbedite uptime data platforme?',
        ctaDescription:
          'Hajde da auditujemo vaš trenutni cloud setup, uklonimo skrivena infrastrukturna uska grla i zajedno izgradimo pouzdanu strategiju upravljanja bez operativnog tereta.',
        ctaButtonText: 'Zatraži audit platforme',
        points: [
          {
            title: 'Cloud infrastruktura i setup',
            description:
              'Postavljamo i strukturiramo sigurna, izolovana cloud okruženja prilagođena potrebama kompanije, uz moderne industrijske bezbednosne standarde.'
          },
          {
            title: '24/7 monitoring i alerting',
            description:
              'Naši sistemi stalno prate pipeline-ove kako bi automatski uhvatili i rešili neočekivane greške, bez zastoja u toku poslovnih podataka.'
          },
          {
            title: 'Skalabilna osnova za buduće tehnologije',
            description:
              'Data platforme održavamo fleksibilnim i spremnim za brzo povezivanje sa novim dashboardima, data science tokovima ili AI alatima.'
          },
          {
            title: 'Kompletno održavanje platforme',
            description:
              'Preuzimamo tehničko vlasništvo nad infrastrukturnim update-ima, database patch-evima i sistemskim migracijama, oslobađajući vaš tim održavanja.'
          },
          {
            title: 'Optimizacija cloud troškova',
            description:
              'Sprovodimo redovne cluster audite, automatsko uspavljivanje neaktivnih resursa i upravljanje storage tier-ovima da stalno smanjujemo mesečne cloud račune.'
          }
        ]
      },
      businessIntelligence: {
        title: 'Business',
        highlightedText: 'Intelligence',
        description:
          'Pretvorite brojeve iz baza u čiste vizuelne dashboarde i automatizovane izveštaje koji menadžmentu pomažu da donosi brze i sigurne poslovne odluke.',
        ctaTag: 'Procena analitike',
        ctaTitle: 'Spremni da sirove podatke pretvorite u jasne metrike?',
        ctaDescription:
          'Hajde da pregledamo trenutne data modele, sredimo reporting slojeve i izgradimo interaktivne dashboarde koji vode pametnije upravljačke odluke.',
        ctaButtonText: 'Optimizuj analitiku',
        points: [
          {
            title: 'Executive dashboardi i KPI tracking',
            description:
              'Dizajniramo interaktivne finansijske i operativne dashboarde visokog uticaja, direktno oko metrika koje vode rast vašeg biznisa.'
          },
          {
            title: 'Jedan izvor istine',
            description:
              'Gradimo centralizovane data modele koristeći moderne alate poput dbt-a, tako da svi timovi gledaju iste brojeve i nema konfliktnih izveštaja.'
          },
          {
            title: 'Sigurna self-service analitika',
            description:
              'Postavljamo kontrolisane access slojeve koji liderima timova omogućavaju da bezbedno istražuju validirane podatke i prave svoje izveštaje.'
          },
          {
            title: 'Automatizovani Slack i Teams alerti',
            description:
              'Povezujemo reporting slojeve direktno sa komunikacionim alatima, uz automatske alerte i uvide tamo gde tim već sarađuje.'
          },
          {
            title: 'Obuka tima i usvajanje platforme',
            description:
              'Ne gradimo samo dashboarde; obezbeđujemo jasnu dokumentaciju i praktičnu obuku kako bi tim pretvorio podatke u svakodnevnu operativnu naviku.'
          }
        ]
      },
      aiMachineLearning: {
        title: 'AI i Machine',
        highlightedText: 'Learning',
        description:
          'Primena veštačke inteligencije i machine learning-a tamo gde donose merljivu poslovnu vrednost, od pametne automatizacije procesa do prediktivnih modela.',
        ctaTag: 'Procena AI strategije',
        ctaTitle: 'Spremni da skalirate operacije uz prilagođene AI modele?',
        ctaDescription:
          'Hajde da pronađemo prilike za automatizaciju, procenimo trening datasete i uvedemo produkciona AI rešenja prilagođena vašoj poslovnoj logici.',
        ctaButtonText: 'Implementiraj AI rešenja',
        points: [
          {
            title: 'Praktično otkrivanje AI use-case-ova',
            description:
              'Radimo tehničku procenu operacija da odvojimo realne AI integracije visoke vrednosti od tržišne buke i hype-a.'
          },
          {
            title: 'Prediktivni i forecasting modeli',
            description:
              'Gradimo machine learning modele koji analiziraju istorijske podatke, predviđaju trendove i ponašanje korisnika i povezuju se direktno sa cloud bazama.'
          },
          {
            title: 'Pametna automatizacija procesa',
            description:
              'Dizajniramo end-to-end automatizaciju koja uklanja ručna uska grla i automatski obrađuje kompleksne data-heavy zadatke velikom brzinom.'
          },
          {
            title: 'Sigurna AI kontrola i guardrails',
            description:
              'Gradimo stroge bezbednosne slojeve i privacy guardrails oko modela, kako bi se AI ponašao predvidljivo i štitio osetljive podatke kompanije.'
          },
          {
            title: 'Kontinuirana optimizacija modela',
            description:
              'Postavljamo automatizovane sisteme koji stalno ažuriraju i proveravaju AI modele, da ostanu tačni i efikasni dok poslovni podaci evoluiraju.'
          }
        ]
      }
    }
  },
  notFound: {
    badge: '404 ruta',
    title: 'Ova stranica nije u pipeline-u.',
    description:
      'Adresa ne odgovara aktivnoj Functor stranici. Vratite se na pouzdanu rutu ili nastavite ka sekcijama koje se najčešće koriste.',
    home: 'Početna',
    back: 'Nazad',
    contact: 'Kontakt',
    redirect: 'Preusmeravanje',
    redirectDescription:
      'Izaberite stabilnu destinaciju i nastavite kroz sajt.',
    quickLinks: [
      { label: 'Data inženjering', path: '/services/data-engineering' },
      {
        label: 'Managed data platforma',
        path: '/services/managed-data-platform'
      },
      { label: 'Studije slučaja', path: '/cases' },
      { label: 'Kontakt', path: '/contact' }
    ]
  },
  errorBoundary: {
    title: 'Ups! Nešto je pošlo po zlu',
    description: 'Došlo je do neočekivane greške. Ne brinite, nije do vas.',
    details: 'Detalji greške (dev režim)',
    tryAgain: 'Pokušaj ponovo',
    reloadPage: 'Učitaj ponovo',
    goHome: 'Na početnu'
  }
};

const es: Translation = {
  common: {
    brand: 'Functor',
    close: 'Cerrar',
    languageNames: {
      en: 'Inglés',
      sr: 'Serbio',
      es: 'Español'
    },
    languageShortNames: {
      en: 'EN',
      sr: 'SR',
      es: 'ES'
    },
    countries: {
      serbia: 'Serbia',
      unitedStates: 'Estados Unidos',
      croatia: 'Croacia'
    },
    nav: {
      ariaLabel: 'Navegación principal',
      homeAriaLabel: 'Inicio de Functor',
      home: 'Inicio',
      services: 'Servicios',
      cases: 'Casos',
      insights: 'Insights',
      about: 'Nosotros',
      partners: 'Partners',
      contact: 'Contacto',
      changeTheme: 'Cambiar tema',
      openMenu: 'Abrir menú',
      languageSelector: 'Seleccionar idioma',
      darkMode: 'Modo oscuro',
      lightMode: 'Modo claro'
    },
    serviceMenu: [
      {
        label: 'Plataforma Datasliv on-prem',
        description: 'Pipelines, data warehouse y calidad de datos.'
      },
      {
        label: 'Ingeniería de datos',
        description: 'Pipelines, data warehouse y calidad de datos.'
      },
      {
        label: 'Plataforma de datos gestionada',
        description: 'Operación y soporte de plataformas cloud.'
      },
      {
        label: 'Business Intelligence',
        description: 'Dashboards, KPIs y analítica self-service.'
      },
      {
        label: 'IA y machine learning',
        description: 'Predicción, automatización e IA responsable.'
      }
    ],
    buttons: {
      bookIntroduction: 'Agenda una introducción',
      discoverExpertise: 'Descubrir experiencia',
      viewFullCaseStudy: 'Ver caso completo',
      browseAllCaseStudies: 'Ver todos los casos',
      seeEngineeringStandards: 'Ver estándares de ingeniería',
      workWithUs: 'Trabaja con nosotros'
    },
    techPartners: [
      'Databricks',
      'Apache Spark',
      'Microsoft Azure',
      'AWS',
      'Snowflake',
      'Scala'
    ]
  },
  pageTitles: {
    home: 'Functor | Consultoría de IA, datos y analítica',
    dataEngineering: 'Ingeniería de datos | Functor',
    managedDataPlatform: 'Plataforma de datos gestionada | Functor',
    businessIntelligence: 'Business Intelligence | Functor',
    aiMachineLearning: 'IA y machine learning | Functor',
    cases: 'Casos | Functor',
    insights: 'Insights | Functor',
    about: 'Nosotros | Functor',
    partners: 'Partners | Functor',
    contact: 'Contacto | Functor',
    notFound: '404 Página no encontrada | Functor',
    error: 'Algo salió mal | Functor'
  },
  footer: {
    description:
      'Functor es una firma de Datos e IA especializada en plataformas modernas de datos, analítica avanzada e ingeniería de alto rendimiento.',
    contactUs: 'Contáctanos',
    allRightsReserved: 'Todos los derechos reservados.'
  },
  backToTop: {
    ariaLabel: (progress: number) =>
      `Volver arriba. Progreso de la página ${progress}%.`
  },
  home: {
    hero: {
      eyebrow: 'Infraestructura moderna de datos e IA empresarial',
      titleLine1: 'Creamos plataformas de datos',
      titleLine2: 'e IA empresarial',
      titleHighlight: 'modernas.',
      description:
        'Functor diseña, construye y mantiene plataformas modernas de datos y sistemas de IA empresarial que te dan control total sobre la infraestructura. Transformamos entornos desordenados en pipelines automatizados, limpios y fiables.',
      capabilitiesAria: 'Ir a la sección de capacidades'
    },
    services: {
      eyebrow: 'Capacidades',
      title: 'Arquitectura de sistemas clave.',
      items: [
        {
          title: 'Ingeniería de datos',
          desc: 'Automatizamos flujos de datos, ordenamos bases y construimos pipelines sólidos para que tus equipos trabajen siempre con números correctos.'
        },
        {
          title: 'Plataforma gestionada',
          desc: 'Monitorización continua, optimización de servidores y mantenimiento completo para garantizar que tus datos nunca se detengan.'
        },
        {
          title: 'Business Intelligence',
          desc: 'Convertimos datos crudos en dashboards claros y visuales que alinean departamentos y ayudan a decidir con rapidez y confianza.'
        },
        {
          title: 'IA y machine learning',
          desc: 'Construimos automatización inteligente, modelos de predicción y flujos de asistentes que generan valor real para el negocio.'
        }
      ]
    },
    cases: {
      titleStart: 'Resultados',
      titleHighlight: 'probados.',
      description:
        'Construimos infraestructura de datos limpia que resuelve cuellos de botella operativos reales. Explora cómo nuestra ingeniería se traduce en menores costes, sistemas más rápidos y decisiones fiables.'
    },
    approach: {
      eyebrow: 'Nuestros principios clave',
      titleStart: 'Flujos de ingeniería enfocados en',
      titleHighlight: 'estabilidad a largo plazo.',
      items: [
        {
          title: 'Codebases fiables',
          text: 'Usamos patrones funcionales limpios para eliminar efectos secundarios ocultos. Así tus pipelines de datos son predecibles y resistentes ante errores de producción.'
        },
        {
          title: 'Integración con el equipo',
          text: 'Trabajamos junto a tus ingenieros internos. Compartimos conocimiento técnico y alineamos objetivos de arquitectura para que los despliegues diarios fluyan sin fricción.'
        },
        {
          title: 'Entrega iterativa',
          text: 'Entregamos infraestructura de forma incremental. Esto permite pruebas constantes, feedback rápido y ajustes seguros a medida que crecen los requisitos.'
        },
        {
          title: 'Escalabilidad a largo plazo',
          text: 'Construimos arquitecturas limpias y desacopladas. Cada plataforma es fácil de probar, eficiente en costes y lista para escalar horizontalmente.'
        }
      ]
    },
    cta: {
      eyebrow: 'Ejecución',
      titleLine1: '¿Listos para optimizar',
      titleLine2: 'su infraestructura de',
      titleHighlight: 'datos',
      titleSuffix: '?',
      description:
        'Diseñemos un plan para ordenar tus plataformas de datos, eliminar errores manuales y optimizar tus inversiones tecnológicas.'
    }
  },
  about: {
    hero: {
      eyebrow: 'Quiénes somos',
      titleLine1: 'Arquitectos de',
      titleHighlight: 'datos',
      description:
        'Somos una firma de Datos e IA especializada en plataformas modernas de datos, analítica avanzada, arquitecturas escalables e ingeniería de alto rendimiento. Reemplazamos complejidad arquitectónica por pipelines resilientes diseñados para cargas pesadas y cumplimiento continuo.'
    },
    stats: {
      metricLabel: 'Métrica de rendimiento',
      items: [
        {
          value: '99.99%',
          label: 'SLA objetivo de pipelines y uptime de producción'
        },
        {
          value: '-40%',
          label: 'Ahorro medio en costes de infraestructura cloud'
        },
        {
          value: '24/7',
          label: 'Monitorización automatizada continua de pipelines'
        }
      ]
    },
    process: {
      eyebrow: 'Marco 4D',
      titleLine1: 'Nuestro enfoque',
      titleHighlight: '4D',
      description:
        'Avanzamos por cuatro fases claras: Discover, Design, Develop y Deliver. Cada paso reduce la ambigüedad, valida la dirección de la plataforma y mantiene la gestión a largo plazo en foco.',
      steps: [
        {
          number: '01',
          title: 'Discover',
          eyebrow: 'Entender el negocio',
          points: [
            'Mapeamos stakeholders, sistemas fuente, problemas de reporting y restricciones operativas para iniciar desde prioridades de negocio, no desde suposiciones.',
            'Convertimos los hallazgos en un roadmap priorizado con riesgos, dependencias y métricas de éxito claras.'
          ]
        },
        {
          number: '02',
          title: 'Design',
          eyebrow: 'Dar forma a la arquitectura',
          points: [
            'Definimos la plataforma objetivo, modelos de datos, patrones de integración, límites de seguridad y modelo de ownership.',
            'Elegimos tecnologías adecuadas al workload y diseñamos para escalabilidad, observabilidad, governance y mantenimiento a largo plazo.',
            'El resultado es un blueprint práctico de implementación que da dirección clara a todos los equipos de entrega.'
          ]
        },
        {
          number: '03',
          title: 'Develop',
          eyebrow: 'Construir y validar',
          points: [
            'Construimos pipelines, transformaciones, componentes de plataforma y automatización en ciclos de entrega enfocados.',
            'Cada release se prueba, revisa, documenta y conecta con monitorización antes de formar parte de producción.',
            'Mantenemos la entrega transparente para que los equipos vean progreso temprano y ajusten prioridades sin perder impulso.'
          ]
        },
        {
          number: '04',
          title: 'Deliver',
          eyebrow: 'Lanzar y mejorar',
          points: [
            'Lanzamos la plataforma con runbooks, monitorización, controles de acceso y handover estructurado.',
            'Formamos a usuarios de negocio y equipos de ingeniería para operar la solución con confianza después del go-live.',
            'Tras la entrega, podemos seguir involucrados mediante servicios gestionados, optimización y desarrollo adicional.'
          ]
        }
      ]
    },
    techStackEyebrow: 'Experiencia en ecosistemas clave',
    locations: {
      eyebrow: 'Operaciones',
      title: 'Oficinas globales'
    }
  },
  partners: {
    hero: {
      eyebrow: 'Red de partners',
      titleLine1: 'Nuestros',
      titleHighlight: 'partners',
      description:
        'Una red enfocada de partners de infraestructura, cloud, ICT y software que nos ayuda a entregar sistemas de datos fiables en entornos enterprise complejos.'
    },
    stats: [
      {
        value: '03',
        label: 'Perfiles activos de partners'
      },
      {
        value: '01',
        label: 'Colaboración reservada'
      },
      {
        value: '360°',
        label: 'Cobertura de entrega'
      }
    ],
    network: {
      title: 'Construido alrededor de ejecución fiable.',
      description:
        'Cada partner ocupa una parte específica de la cadena de entrega: infraestructura, plataformas gestionadas, ecosistemas ICT o product engineering.'
    },
    lanes: [
      {
        title: 'Profundidad de infraestructura',
        description:
          'Hosting, conectividad y operaciones de plataforma para workloads de datos que necesitan comportamiento productivo predecible.'
      },
      {
        title: 'Alcance enterprise',
        description:
          'Ecosistemas ICT y capacidad regional de entrega para organizaciones con entornos técnicos distribuidos.'
      },
      {
        title: 'Product engineering',
        description:
          'Capacidad de entrega de software que complementa plataformas de datos con capas de aplicación fiables.'
      }
    ],
    labels: {
      activeNetwork: 'Alcance de colaboración',
      activeNetworkDescription:
        'Cobertura combinada de hosting, ICT enterprise e ingeniería de software.',
      partnerRole: 'Rol del partner',
      collaboration: 'Foco de colaboración',
      region: 'Región',
      visitWebsite: 'Visitar sitio'
    },
    items: {
      mainstream: {
        role: 'Partner de cloud soberano y hosting',
        focus:
          'Despliegues DataSliv, operaciones de infraestructura y rutas de hosting listas para producción.',
        region: 'Europa'
      },
      axians: {
        role: 'Partner ICT y tecnología enterprise',
        focus:
          'Conectividad, cloud, data center y ecosistemas de transformación digital.',
        region: 'Internacional'
      },
      ingsoftware: {
        role: 'Partner de ingeniería de software',
        focus:
          'Custom software delivery, equipos de producto e ingeniería de aplicaciones para plataformas enterprise.',
        region: 'Europa y mercados globales'
      },
      orion: {
        role: 'Partner de telecom, cloud y data center',
        focus:
          'Conectividad, infraestructura cloud y servicios ICT de negocio preparados para un futuro perfil público de partner.',
        region: 'Serbia'
      }
    }
  },
  contact: {
    hero: {
      eyebrow: 'Contacto',
      titleStart: 'Ponte en',
      titleHighlight: 'contacto',
      description:
        '¿Listos para construir? Cuéntanos sobre tu infraestructura de datos u objetivos de IA y trazaremos una solución de ingeniería práctica.'
    },
    form: {
      title: 'Envíanos un mensaje',
      successMessage:
        '¡Gracias! Tu mensaje se ha enviado. Te responderemos pronto.',
      successToast: 'Tu mensaje se ha enviado. Te responderemos pronto.',
      notConfigured:
        'El formulario de contacto aún no está configurado. Añade VITE_GOOGLE_EMAILER_URL al entorno.',
      sendError:
        'No pudimos enviar tu mensaje ahora. Inténtalo de nuevo o escríbenos directamente.',
      honeypotLabel: 'Deja este campo vacío',
      labels: {
        firstName: 'Nombre *',
        lastName: 'Apellido *',
        email: 'E-mail *',
        message: 'Mensaje *'
      },
      placeholders: {
        firstName: 'Tu nombre',
        lastName: 'Tu apellido',
        email: 'tu@email.com',
        message: 'Cuéntanos cómo podemos ayudar...'
      },
      validation: {
        firstName: 'El nombre es obligatorio.',
        lastName: 'El apellido es obligatorio.',
        email: 'El e-mail es obligatorio.',
        message: 'El mensaje es obligatorio.',
        invalidEmail: 'Introduce un e-mail válido.',
        fillRequired: 'Completa todos los campos obligatorios.'
      },
      sending: 'Enviando...',
      send: 'Enviar'
    },
    directChannels: {
      title: 'Canales directos',
      description:
        'Contacta directamente con el equipo adecuado o elige la oficina más cercana.',
      officesTitle: 'Oficinas',
      departments: [
        {
          title: 'Ventas',
          description: 'Para consultas de negocio y partnerships',
          email: 'sales@functor.com'
        },
        {
          title: 'Soporte',
          description: 'Soporte técnico y atención al cliente',
          email: 'support@functor.com'
        },
        {
          title: 'Carreras',
          description: 'Únete a nuestro equipo en crecimiento',
          email: 'careers@functor.com'
        }
      ]
    }
  },
  insights: {
    hero: {
      eyebrow: 'Insights',
      titleStart: 'Pensamiento de datos',
      titleHighlight: 'aplicado.',
      description:
        'Notas de campo, lecciones de arquitectura y perspectivas prácticas del equipo de Functor, conectadas con los servicios y casos detrás del trabajo.'
    },
    labels: {
      dateUnavailable: 'Fecha pendiente',
      fallbackTag: 'Functor Insight',
      minuteRead: (minutes: number) => `${minutes} min de lectura`,
      openArticle: 'Abrir artículo',
      related: 'Páginas relacionadas'
    },
    filters: {
      searchLabel: 'Buscar insights',
      searchPlaceholder: 'Buscar insights',
      topicLabel: 'Tema',
      allTopics: 'Todos los temas',
      clear: 'Limpiar filtros',
      results: (start: number, end: number, total: number) =>
        total ? `Mostrando ${start}-${end} de ${total}` : 'Sin resultados',
      noResultsTitle: 'Ningún insight coincide con estos filtros.',
      noResultsDescription: 'Prueba otra búsqueda o limpia los filtros.'
    },
    pagination: {
      previous: 'Anterior',
      next: 'Siguiente',
      page: (currentPage: number, totalPages: number) =>
        `Página ${currentPage} de ${totalPages}`
    },
    states: {
      notConfiguredTitle: 'Insights está casi listo.',
      notConfiguredDescription:
        'Los artículos publicados aparecerán aquí en cuanto el feed del blog esté conectado.',
      errorTitle: 'Insights no pudo cargarse.',
      errorDescription:
        'La fuente del blog no respondió. Inténtalo de nuevo en un momento.',
      retry: 'Recargar feed',
      emptyTitle: 'Aún no hay insights publicados.',
      emptyDescription:
        'Los nuevos artículos aparecerán aquí cuando estén disponibles desde el blog.'
    },
    article: {
      backToInsights: 'Volver a Insights',
      errorTitle: 'El artículo no pudo cargarse.',
      errorDescription:
        'El artículo seleccionado no está disponible desde la API de contenido en este momento.',
      notFoundTitle: 'Artículo no encontrado.',
      notFoundDescription:
        'Este insight no está publicado para el sitio o idioma actual.'
    },
    relatedLinks: {
      dataSliv: 'Plataforma DataSliv',
      dataEngineering: 'Ingeniería de datos',
      managedDataPlatform: 'Plataforma de datos gestionada',
      businessIntelligence: 'Business Intelligence',
      aiMachineLearning: 'IA y machine learning',
      cases: 'Casos',
      contact: 'Contacto'
    },
    cta: {
      eyebrow: 'Siguiente paso',
      title: 'Convierte las ideas en un roadmap operativo.',
      description:
        'Combina el pensamiento más reciente con experiencia productiva de nuestro trabajo de entrega.',
      cases: 'Ver casos',
      contact: 'Iniciar conversación'
    }
  },
  cases: {
    hero: {
      eyebrow: 'Casos de estudio',
      titleStart: 'Nuestros',
      titleHighlight: 'casos',
      description:
        'Implementaciones soberanas probadas. Eliminamos cuellos de botella de sistemas legacy complejos, ejecutamos repatriación cloud a gran escala y entregamos auditoría de cumplimiento robusta.'
    },
    filters: {
      label: 'Filtros',
      ariaLabel: 'Categorías de casos de estudio'
    },
    labels: {
      challenge: 'El reto',
      solution: 'La solución',
      visitWebsite: 'Visitar',
      ndaSecure: 'NDA SEGURO'
    },
    categoryLabels: {
      All: 'Todos',
      Migration: 'Migración',
      'Data Engineering': 'Ingeniería de datos',
      'Cloud Infrastructure': 'Infraestructura cloud',
      'Streaming & AI': 'Streaming e IA',
      'Banking & Finance': 'Banca y finanzas'
    },
    studies: {
      'cropt-satellite-ml-pipeline': {
        title:
          'Lakehouse de imágenes satelitales de alto volumen para Cropt AgTech',
        challenge:
          'Procesar petabytes de imágenes satelitales multiespectrales y datos geoespaciales para analítica agrícola y modelos predictivos de ML.',
        solution:
          'Construimos una plataforma Delta Lakehouse escalable que automatiza ingesta, transformación y almacenamiento de datos satelitales, habilitando monitoreo de cultivos, feature engineering y flujos de machine learning.',
        metrics: [
          { label: 'Velocidad de entrenamiento', value: '15x' },
          { label: 'Estándar SLA', value: '99.9%' }
        ],
        tags: [
          'AgTech Lakehouse',
          'Ingesta satelital',
          'Aceleración de modelos',
          'Capas GeoJSON',
          'Delta Lake'
        ],
        placeholderLabel: ''
      },
      'design-software-dwh-analytics-optimization': {
        title:
          'Ingeniería de datos y analítica para una empresa global de software de diseño',
        challenge:
          'Jobs de data warehouse poco fiables, propensos a errores y no idempotentes generaban reporting inconsistente, capas SQL lentas, múltiples versiones de la verdad, manejo débil de outliers y KPIs de gestión faltantes.',
        solution:
          'Rediseñamos jobs DWH y capas de reporting para que fueran idempotentes, precisos y más rápidos; introdujimos una versión única de la verdad, automatizamos capas reutilizables, aplicamos métodos estadísticos sólidos para outliers y reconstruimos reporting de gestión alrededor de los KPIs importantes.',
        metrics: [
          { label: 'Jobs DWH', value: 'Fiables' },
          { label: 'Capa KPI', value: 'Precisa' }
        ],
        tags: [
          'Software de diseño',
          'Optimización DWH',
          'Analítica KPI',
          'Versión única de la verdad',
          'Métodos de outliers'
        ],
        placeholderLabel: ''
      },
      'inspira-group-cloud-repatriation': {
        title: 'Repatriación cloud y optimización de costes e-commerce',
        challenge:
          'Mantener analítica, reporting y procesamiento de eventos estables en un portafolio creciente de productos digitales de alto tráfico, controlando gasto de infraestructura y riesgo de entrega.',
        solution:
          'Mantuvimos y optimizamos la plataforma de datos alrededor de DataSliv, streams de Kafka y analítica batch para que los equipos de producto contaran con reporting más fresco y costes previsibles.',
        metrics: [
          { label: 'Reducción de costes', value: '18x' },
          { label: 'Paridad de workloads', value: '100%' }
        ],
        tags: [
          'Repatriación cloud',
          'DataSliv',
          'BI e-commerce',
          'Kafka Streams',
          'Almacenamiento HDFS'
        ],
        placeholderLabel: ''
      },
      'asml-data-modernization': {
        title:
          'Migración de Cloudera a Azure para una de las mayores empresas de semiconductores',
        challenge:
          'Mover workloads industriales mission-critical desde un entorno on-premises de Cloudera preservando fiabilidad, lineage y estándares de procesamiento para analítica de ingeniería compleja.',
        solution:
          'Planificamos y ejecutamos la ruta de migración hacia Azure y Databricks, rediseñando layouts de datos y patrones Spark para operaciones cloud lakehouse escalables.',
        metrics: [
          { label: 'Cambio de plataforma', value: 'Azure' },
          { label: 'Lakehouse', value: 'Databricks' }
        ],
        tags: [
          'Migración Cloudera',
          'Microsoft Azure',
          'Databricks',
          'Datos de semiconductores',
          'Lakehouse'
        ],
        placeholderLabel: 'CHIP INFRA'
      },
      'a1-serbia-dwh-migration': {
        title:
          'Migración Oracle-to-DataSliv para un gran operador telco balcánico',
        challenge:
          'Modernizar un data warehouse Oracle on-premises donde consultas analíticas lentas, flujos de ingesta frágiles y governance limitado hacían difícil confiar en el reporting diario.',
        solution:
          'Migramos workloads clave de ingesta, storage, analítica y governance desde sistemas Oracle on-premises a un despliegue DataSliv diseñado para datos operativos a escala telco.',
        metrics: [
          { label: 'Aceleración de consultas', value: '5x más rápido' },
          { label: 'Soberanía de datos', value: '100%' }
        ],
        tags: [
          'DataSliv',
          'Migración Oracle',
          'Gran telco',
          'On-premises',
          'Analítica telecom'
        ],
        placeholderLabel: 'BALKAN TELCO'
      },
      'banca-intesa-compliance-lakehouse': {
        title:
          'Big data lake de cumplimiento regulatorio estricto para un gran banco con 2M+ clientes',
        challenge:
          'Arquitectar y escalar un enorme data lake financiero en GCP cumpliendo los estándares más estrictos de banca central.',
        solution:
          'Aplicamos patrones expertos de data engineering para transparencia total de pipelines, auditoría de datos multicapa y frameworks nativos de cumplimiento NBS/EBA.',
        metrics: [
          { label: 'SLA bancario', value: '99.99%' },
          { label: 'Auditoría regulatoria', value: 'Aprobada' }
        ],
        tags: [
          'Data lake financiero',
          'Arquitectura GCP',
          'Cumplimiento bancario',
          '2M+ clientes',
          'NBS / EBA',
          'Auditoría de datos'
        ],
        placeholderLabel: 'GRAN BANCO'
      },
      'yettel-kickoff-consulting': {
        title: 'Mantenimiento de data lake para una gran telco',
        challenge:
          'Mantener un data lake telecom de alto volumen estable, observable y listo para analítica diaria mientras aumentaban volumen de ingesta y demandas de reporting downstream.',
        solution:
          'Proporcionamos mantenimiento continuo del data lake, hardening de pipelines, tuning de rendimiento y soporte operativo para que los equipos internos contaran con datasets frescos y predecibles.',
        metrics: [
          { label: 'Data lake', value: 'Mantenido' },
          { label: 'Operación de pipelines', value: 'Estable' }
        ],
        tags: [
          'Gran telco',
          'Mantenimiento de data lake',
          'Operación de pipelines',
          'Tuning de rendimiento',
          'Soporte operativo'
        ],
        placeholderLabel: 'ENTERPRISE DWH'
      }
    }
  },
  services: {
    detail: {
      backToHome: 'Volver al inicio',
      eyebrow: 'Servicio',
      bookIntroduction: 'Agenda una introducción',
      viewCases: 'Ver casos',
      whatWeDeliver: 'Qué entregamos',
      deliverTitle: 'Sistemas estables, no experimentos.',
      capabilities: 'Capacidades',
      capabilitiesTitle:
        'Bloques de ingeniería que hacen fiable tu plataforma de datos.',
      included: 'Incluido',
      defaultCtaTag: 'Ejecución',
      defaultCtaTitle: '¿Listos para modernizar tu infraestructura de datos?',
      defaultCtaDescription:
        'Podemos revisar tu setup actual, identificar los mayores cuellos de botella y trazar un plan práctico para pipelines fiables, storage más limpio y menores costes operativos.',
      defaultCtaButton: 'Iniciar proyecto'
    },
    pages: {
      dataEngineering: {
        title: 'Ingeniería',
        highlightedText: 'de datos',
        description:
          'Diseñamos y construimos plataformas de datos modernas y estables que ayudan a tu negocio a manejar grandes volúmenes de información sin caídas, retrasos ni altos costes cloud.',
        points: [
          {
            title: 'Pipelines de datos fiables',
            description:
              'Construimos pipelines automatizados que mueven datos entre tus sistemas, storage cloud y bases de datos sin pérdida ni duplicación.'
          },
          {
            title: 'Data warehouses y lakehouses modernos',
            description:
              'Organizamos el almacenamiento de la empresa con estándares cloud modernos. Los datos se mantienen limpios, accesibles y económicos incluso bajo carga pesada.'
          },
          {
            title: 'Pruebas automatizadas de calidad de datos',
            description:
              'Nuestros sistemas revisan continuamente errores o cambios inesperados para que la analítica y los reportes sean siempre precisos.'
          },
          {
            title: 'Procesamiento de datos de alto rendimiento',
            description:
              'Con herramientas como Apache Spark y Scala, construimos sistemas que limpian y agregan datasets masivos dentro de tus plazos.'
          },
          {
            title: 'Seguridad empresarial y governance',
            description:
              'Protegemos tu infraestructura con cifrado estricto y controles de acceso automatizados, manteniendo seguros los datos de clientes y el cumplimiento regulatorio.'
          }
        ]
      },
      managedDataPlatform: {
        title: 'Plataforma de datos',
        highlightedText: 'gestionada',
        description:
          'Mantén tu infraestructura de datos lista para producción 24/7. Nos encargamos de monitorización, escalado y mantenimiento para que tu equipo se enfoque en valor de negocio.',
        ctaTag: 'Evaluación de plataforma',
        ctaTitle: '¿Listos para asegurar el uptime de tu plataforma?',
        ctaDescription:
          'Auditemos tu setup cloud actual, eliminemos cuellos de botella ocultos y construyamos juntos una estrategia de gestión fiable y sin carga operativa.',
        ctaButtonText: 'Solicitar auditoría',
        points: [
          {
            title: 'Infraestructura cloud y setup',
            description:
              'Desplegamos y estructuramos entornos cloud seguros y aislados, adaptados a las necesidades de tu empresa y con estándares modernos de seguridad.'
          },
          {
            title: 'Monitorización y alertas 24/7',
            description:
              'Nuestros sistemas monitorean pipelines constantemente para detectar y corregir errores inesperados de forma automática.'
          },
          {
            title: 'Base escalable para tecnología futura',
            description:
              'Mantenemos tus plataformas flexibles y listas para conectar nuevos dashboards, flujos de data science o herramientas de IA cuando lo necesites.'
          },
          {
            title: 'Mantenimiento completo de plataforma',
            description:
              'Tomamos ownership técnico de actualizaciones, parches de bases de datos y migraciones, liberando a tu equipo de carga de mantenimiento.'
          },
          {
            title: 'Optimización de costes cloud',
            description:
              'Ejecutamos auditorías de clúster, horarios automáticos de suspensión y gestión de tiers de storage para reducir continuamente tus facturas cloud.'
          }
        ]
      },
      businessIntelligence: {
        title: 'Business',
        highlightedText: 'Intelligence',
        description:
          'Transforma números de bases de datos en dashboards claros y reportes automatizados que ayudan al liderazgo a tomar decisiones rápidas y seguras.',
        ctaTag: 'Evaluación analítica',
        ctaTitle: '¿Listos para convertir datos crudos en métricas claras?',
        ctaDescription:
          'Revisemos tus modelos actuales, limpiemos las capas de reporting y construyamos dashboards interactivos que impulsen mejores decisiones de gestión.',
        ctaButtonText: 'Optimizar analítica',
        points: [
          {
            title: 'Dashboards ejecutivos y tracking KPI',
            description:
              'Diseñamos dashboards financieros y operativos interactivos de alto impacto, centrados en las métricas que impulsan el crecimiento.'
          },
          {
            title: 'Fuente única de verdad',
            description:
              'Construimos modelos centralizados con herramientas modernas como dbt para que todos los departamentos vean los mismos números.'
          },
          {
            title: 'Analítica self-service segura',
            description:
              'Configuramos capas de acceso controladas para que líderes de equipo exploren datos validados y creen reportes sin romper nada.'
          },
          {
            title: 'Alertas automatizadas en Slack y Teams',
            description:
              'Conectamos tus capas de reporting con herramientas de comunicación diaria, enviando alertas e insights justo donde el equipo colabora.'
          },
          {
            title: 'Formación del equipo y adopción',
            description:
              'No solo construimos dashboards; ofrecemos documentación clara y formación práctica para convertir los datos en hábito operativo diario.'
          }
        ]
      },
      aiMachineLearning: {
        title: 'IA y Machine',
        highlightedText: 'Learning',
        description:
          'Aplicamos inteligencia artificial y machine learning donde generan valor medible, desde automatización inteligente hasta modelos predictivos.',
        ctaTag: 'Evaluación de estrategia IA',
        ctaTitle: '¿Listos para escalar operaciones con modelos IA a medida?',
        ctaDescription:
          'Identifiquemos oportunidades de automatización, evaluemos datasets de entrenamiento y despleguemos soluciones IA productivas adaptadas a tu lógica de negocio.',
        ctaButtonText: 'Desplegar soluciones IA',
        points: [
          {
            title: 'Descubrimiento práctico de casos IA',
            description:
              'Realizamos una evaluación técnica para separar integraciones IA realistas y valiosas del ruido especulativo del mercado.'
          },
          {
            title: 'Modelos predictivos y forecasting',
            description:
              'Construimos modelos que analizan datos históricos para prever tendencias, comportamiento de clientes e integrarse con tus bases cloud.'
          },
          {
            title: 'Automatización inteligente de flujos',
            description:
              'Diseñamos sistemas end-to-end que eliminan cuellos de botella manuales y gestionan tareas complejas de datos a alta velocidad.'
          },
          {
            title: 'Control IA seguro y guardrails',
            description:
              'Construimos capas de seguridad y privacidad alrededor de los modelos para que la IA se comporte de forma predecible y proteja datos sensibles.'
          },
          {
            title: 'Optimización continua de modelos',
            description:
              'Configuramos sistemas automatizados que actualizan y revisan modelos para mantener precisión y eficiencia a medida que evolucionan los datos.'
          }
        ]
      }
    }
  },
  notFound: {
    badge: 'Ruta 404',
    title: 'Esta página no está en el pipeline.',
    description:
      'La dirección no coincide con una página activa de Functor. Vuelve a una ruta fiable o continúa hacia las secciones más usadas.',
    home: 'Inicio',
    back: 'Atrás',
    contact: 'Contacto',
    redirect: 'Redirección',
    redirectDescription:
      'Elige un destino estable y sigue navegando por el sitio.',
    quickLinks: [
      { label: 'Ingeniería de datos', path: '/services/data-engineering' },
      {
        label: 'Plataforma de datos gestionada',
        path: '/services/managed-data-platform'
      },
      { label: 'Casos de estudio', path: '/cases' },
      { label: 'Contacto', path: '/contact' }
    ]
  },
  errorBoundary: {
    title: '¡Ups! Algo salió mal',
    description:
      'Encontramos un error inesperado. No te preocupes, no es culpa tuya.',
    details: 'Detalles del error (modo dev)',
    tryAgain: 'Intentar de nuevo',
    reloadPage: 'Recargar página',
    goHome: 'Ir al inicio'
  }
};

export const translations: Record<Language, Translation> = {
  en,
  sr,
  es
};
